import React, { useEffect, useMemo, useRef, useState } from 'react'

import { Link, useMediaQuery } from '@chakra-ui/react'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { useDeleteApplyV2ProfileMutation } from '@core/app/api/applyJobV2Api'
import {
    useGetApplicationDetailsV2Query,
    useGetPostApplySettingsV2Query,
    useGetPostInfoPreviewV2Query,
    useGetPostInfoV2Query,
} from '@core/app/api/postsApi'
import { useGetHeaderInfoQuery } from '@core/app/api/profileApi'
import { Localization } from '@core/app/types/globalApiType'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import Button from '@core/components/ApplyDialogV2/v2/Button'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import useLocalStorageStore from '@core/utils/hooks/useLocalStorageStore'

import { Page404 } from '@page/404/404'
import PostPageV2About from '@page/post-v2/components/PostPageV2About'
import PostPageV2ContactPersons from '@page/post-v2/components/PostPageV2ContactPersons'
import PostPageV2Content from '@page/post-v2/components/PostPageV2Content'
import PostPageV2Files from '@page/post-v2/components/PostPageV2Files'
import PostPageV2Header2 from '@page/post-v2/components/PostPageV2Header2'
import PostPageV2Images from '@page/post-v2/components/PostPageV2Images'
import PostPageV2Industries from '@page/post-v2/components/PostPageV2Industries'
import PostPageV2Info from '@page/post-v2/components/PostPageV2Info'
import PostPageV2Video from '@page/post-v2/components/PostPageV2Video'
import './PostPageV2.scss'

const baseClass = 'PostPageV2'

const headerBaseClass = 'PostPageV2Header2'

function PostPageV2() {
    const [searchParams, setSearchParams] = useSearchParams()
    const [serviceCode] = useLocalStorageStore('service_code')
    const applyBtnRef = useRef(null)
    const { onOpenApply, onCloseApply, isOpen } = useApplyV2Context()
    const applyVersion = searchParams.get('apply')
    const [localization, setLocalization] = useState<Localization>({})
    const { id } = useParams()
    const numberId = useMemo(() => {
        const n = Number(id)
        return isNaN(n) ? 0 : n
    }, [id])
    const {
        data: headerData,
        isLoading: isHeaderInfoLoading,
        isFetching: isHeaderInfoFetching,
    } = useGetHeaderInfoQuery(undefined, { skip: !numberId })
    const isHeaderInfoReady = !isHeaderInfoLoading && !isHeaderInfoFetching
    const candidateId = headerData?.data?.CandidateID ?? 0
    const applyV2Key = useMemo(() => `apply_dialog_v2-${numberId}-${candidateId}` as const, [numberId, candidateId])
    const [storageData, _, removeStorageData] = useLocalStorageStore(applyV2Key)
    const [deleteApplyV2Profile, { isLoading: isProfileDeleteLoading }] = useDeleteApplyV2ProfileMutation()
    const [isDesktop] = useMediaQuery('(min-width: 1230px)', {
        fallback: false,
    })
    const { t } = useTranslation()
    const isPreview = window.location.href.includes('job/preview?')
    const isApplyOnly = location.href.includes('apply_only')
    const hasOpenedApplyOnlyRef = useRef(false)
    const previewQuery = useGetPostInfoPreviewV2Query({ getParams: window.location.search }, { skip: !isPreview })
    const postQuery = useGetPostInfoV2Query({ id: numberId }, { skip: isPreview || !numberId })
    const {
        data: postInfoData,
        isError: isPostInfoError,
        isLoading: isPostInfoLoading,
        isFetching: isPostInfoFetching,
        error: postInfoError,
    } = isPreview ? previewQuery : postQuery

    const { data: postApplySettingsData } = useGetPostApplySettingsV2Query({ id: numberId })
    const {
        data: applicationDetailsData,
        isLoading: isApplicationDetailsLoading,
        isFetching: isApplicationDetailsFetching,
    } = useGetApplicationDetailsV2Query(
        { id: postApplySettingsData?.data?.application?.id },
        { skip: !postApplySettingsData?.data?.application?.id }
    )

    const isApplicationDetailsLoader = isApplicationDetailsLoading || isApplicationDetailsFetching
    const isPostInfoLoader = isPostInfoLoading || isPostInfoFetching || isApplicationDetailsLoader

    const isNotFoundError =
        Array.isArray(postInfoError?.data?.errors) &&
        postInfoError?.data?.errors?.some((error: any) => error.code === 1)

    useEffect(() => {
        if (isPreview && onCloseApply) {
            onCloseApply()
        }
    }, [isPreview, onCloseApply])

    useEffect(() => {
        if (!isApplyOnly) {
            hasOpenedApplyOnlyRef.current = false
            return
        }

        if (!hasOpenedApplyOnlyRef.current && !isOpen && isHeaderInfoReady && !isPostInfoLoader) {
            onOpenApply()
            hasOpenedApplyOnlyRef.current = true
        }
    }, [isApplyOnly, isHeaderInfoReady, isOpen, onOpenApply, isPostInfoLoader])

    const applicationStatusId = applicationDetailsData?.data?.application?.statusId

    const { isArchived, isEmployeesOnly, isExpired, isExternal } = postInfoData?.data || {}
    const unavailableStatusId = isArchived ? 3 : isExpired ? 1 : isEmployeesOnly ? 2 : 0
    const isUnavailable = unavailableStatusId > 0
    const hasHeaderBackground = Boolean(
        postInfoData?.data?.cover?.backgroundImage || postInfoData?.data?.cover?.color || postInfoData?.data?.logo
    )
    // console.log(isArchived, isEmployeesOnly, isExpired, isExternal)

    // Department Ingress placement: Top → showBottom false; Bottom → showBottom true (same as old PostInfo)
    const showCompanyAtBottom = Boolean(postInfoData?.data?.company?.showBottom)

    const aboutSection = <PostPageV2About postInfo={postInfoData} isLoading={isPostInfoLoader} />

    const applyButtonTitle = useMemo(() => {
        if (storageData) return t('11868', 'Continue application')
        return localization[30]
            ? localization[30]
            : t('6634', 'Apply now')
                  .toLowerCase()
                  .replace(/^\w/, (c) => c.toUpperCase())
    }, [localization, storageData, t])

    const onApply = () => {
        if (postInfoData?.data?.isExternal && postInfoData?.data?.externalLink && isExternal) {
            window.open(postInfoData.data.externalLink)
        } else {
            onOpenApply()
        }
    }

    const isProductionCondition =
        !isUnavailable &&
        !isApplicationDetailsLoading &&
        applicationStatusId !== 0 &&
        applicationStatusId !== 1 &&
        applicationStatusId !== 2 &&
        applicationStatusId !== 3 &&
        applicationStatusId !== 4

    const isLocalCondition =
        process.env.ENV === 'local' &&
        !isUnavailable &&
        applicationStatusId !== undefined &&
        [0, 1, 2, 3, 4].includes(applicationStatusId)

    const showButton = isProductionCondition || isLocalCondition

    const statusNotice = (isUnavailable || isExternal) && (
        <div
            className={clsx(`${baseClass}__unavailable`, {
                [`${baseClass}__unavailable--archived`]: unavailableStatusId === 3,
                [`${baseClass}__unavailable--employees`]: unavailableStatusId === 2,
                [`${baseClass}__unavailable--expired`]: unavailableStatusId === 1,
                [`${baseClass}__unavailable--external`]: isExternal,
            })}
        >
            <div className={`${baseClass}__unavailable__icon`}>
                {unavailableStatusId === 1 && (
                    <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M67.1969 35.9969C67.1969 44.7181 63.6276 52.5796 57.9046 58.2299C52.3046 63.8189 44.5507 67.1969 35.9969 67.1969C27.5046 67.1969 19.7507 63.7575 14.0892 58.2299C8.3661 52.5796 4.79688 44.7181 4.79688 35.9969C4.79688 18.7386 18.7661 4.79688 35.9969 4.79688C53.2276 4.79688 67.1969 18.8 67.1969 35.9969Z"
                            stroke="#C05E1D"
                            strokeWidth="0.75"
                            strokeMiterlimit="10"
                        />
                        <path
                            d="M53.0399 20.6406C55.5252 20.6406 57.5399 22.6553 57.5399 25.1406V56.7733C57.5399 57.9668 57.0765 59.1237 56.1817 59.9135C50.812 64.6532 43.7392 67.4709 36.0004 67.4709C28.3272 67.4709 21.2671 64.6093 15.8533 59.9193C14.9383 59.1266 14.4609 57.9549 14.4609 56.7443V25.1406C14.4609 22.6553 16.4757 20.6406 18.9609 20.6406H53.0399Z"
                            fill="white"
                            stroke="#C05E1D"
                            strokeWidth="0.75"
                            strokeMiterlimit="10"
                        />
                        <path
                            d="M45.0929 46.8516H25.8525C25.084 46.8516 24.4609 47.4746 24.4609 48.2432C24.4609 49.0117 25.084 49.6348 25.8525 49.6348H45.0929C45.8614 49.6348 46.4845 49.0117 46.4845 48.2432C46.4845 47.4746 45.8614 46.8516 45.0929 46.8516Z"
                            fill="#C05E1D"
                        />
                        <path
                            d="M45.0929 52.9609H25.8525C25.084 52.9609 24.4609 53.584 24.4609 54.3525C24.4609 55.1211 25.084 55.7441 25.8525 55.7441H45.0929C45.8614 55.7441 46.4845 55.1211 46.4845 54.3525C46.4845 53.584 45.8614 52.9609 45.0929 52.9609Z"
                            fill="#C05E1D"
                        />
                        <path
                            d="M39.7137 59.1328H31.2432C30.4746 59.1328 29.8516 59.7559 29.8516 60.5244C29.8516 61.293 30.4746 61.916 31.2432 61.916H39.7137C40.4823 61.916 41.1053 61.293 41.1053 60.5244C41.1053 59.7559 40.4823 59.1328 39.7137 59.1328Z"
                            fill="#C05E1D"
                        />
                        <path
                            d="M30.1988 36.4029L34.9429 28.5525C35.4593 27.8158 36.5404 27.8158 37.0568 28.5525L41.8009 36.4029C42.3011 37.1068 41.8009 38.1053 40.9456 38.1053H31.0702C30.1988 38.0889 29.6986 37.1068 30.1988 36.4029Z"
                            fill="#C05E1D"
                            stroke="#C05E1D"
                            strokeWidth="0.315789"
                            strokeMiterlimit="10"
                        />
                        <path
                            d="M35.999 30.4043C36.5546 30.4043 36.9654 30.8924 36.9268 31.4766V31.4795L36.6758 34.4062L36.6748 34.4053C36.6519 34.7666 36.3585 35.0742 35.999 35.0742C35.6395 35.0742 35.3451 34.7667 35.3223 34.4053L35.0713 31.4795V31.4766C35.0325 30.9121 35.4415 30.4043 35.999 30.4043Z"
                            fill="white"
                            stroke="#C05E1D"
                            strokeWidth="0.315789"
                        />
                        <path
                            d="M36.001 35.2949C36.4341 35.2949 36.7617 35.6665 36.7617 36.1035C36.7617 36.5405 36.4341 36.9121 36.001 36.9121C35.5679 36.912 35.2403 36.5404 35.2402 36.1035C35.2402 35.6666 35.5679 35.295 36.001 35.2949Z"
                            fill="white"
                            stroke="#C05E1D"
                            strokeWidth="0.315789"
                        />
                    </svg>
                )}
                {unavailableStatusId === 3 && (
                    <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M62.4016 32.0468V53.0388C62.4016 57.4001 58.8544 60.9472 54.435 60.9472H17.5681C13.2068 60.9472 9.65971 57.4582 9.60156 53.097C9.60156 53.0388 9.60156 53.0388 9.60156 52.9807V32.0468C9.60156 31.9886 9.60156 31.9886 9.60156 31.9305C9.60156 31.8142 9.60156 31.6979 9.65971 31.5816C9.71786 31.4071 9.77601 31.2908 9.83416 31.1164L19.7196 12.2177C20.0685 11.4617 20.8245 11.0547 21.6386 11.0547H50.3064C51.1205 11.0547 51.8183 11.4617 52.2254 12.2177L62.1108 31.1164C62.169 31.2327 62.2271 31.4071 62.2853 31.5816C62.4016 31.6979 62.4016 31.8723 62.4016 32.0468Z"
                            fill="#FFF7E6"
                            stroke="#CC8B00"
                            strokeWidth="0.75"
                            strokeMiterlimit="10"
                        />
                        <path
                            d="M62.4016 32.0511V55.1947C62.4016 58.393 59.843 60.9516 56.5866 60.9516H15.4165C12.2183 60.9516 9.60156 58.393 9.60156 55.1947V31.9348C9.60156 31.8185 9.60156 31.7022 9.65971 31.5859H22.9179C24.895 31.5859 26.5231 33.156 26.5231 35.1912C26.5231 36.1798 26.9302 37.1102 27.5698 37.7498C28.2676 38.4476 29.0817 38.7965 30.1284 38.7965H41.9328C43.9099 38.7965 45.5381 37.2265 45.5381 35.1912C45.5381 34.2027 45.9452 33.2723 46.5848 32.6326C47.2826 31.9348 48.0967 31.5859 49.0853 31.5859H62.2853C62.4016 31.7022 62.4016 31.8767 62.4016 32.0511Z"
                            fill="white"
                            stroke="#CC8B00"
                            strokeWidth="0.75"
                            strokeMiterlimit="10"
                        />
                    </svg>
                )}
                {unavailableStatusId === 2 && (
                    <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M67.1969 35.9969C67.1969 44.7181 63.6276 52.5796 57.9046 58.2299C52.3046 63.8189 44.5507 67.1969 35.9969 67.1969C27.5046 67.1969 19.7507 63.7575 14.0892 58.2299C8.3661 52.5796 4.79688 44.7181 4.79688 35.9969C4.79688 18.7386 18.7661 4.79688 35.9969 4.79688C53.2276 4.79688 67.1969 18.8 67.1969 35.9969Z"
                            stroke="#CC8B00"
                            strokeWidth="0.75"
                            strokeMiterlimit="10"
                        />
                        <path
                            d="M53.0399 20.6406C55.5252 20.6406 57.5399 22.6553 57.5399 25.1406V56.7733C57.5399 57.9668 57.0765 59.1237 56.1817 59.9135C50.812 64.6532 43.7392 67.4709 36.0004 67.4709C28.3272 67.4709 21.2671 64.6093 15.8533 59.9193C14.9383 59.1266 14.4609 57.9549 14.4609 56.7443V25.1406C14.4609 22.6553 16.4757 20.6406 18.9609 20.6406H53.0399Z"
                            fill="white"
                            stroke="#CC8B00"
                            strokeWidth="0.75"
                            strokeMiterlimit="10"
                        />
                        <path
                            d="M45.0929 46.8516H25.8525C25.084 46.8516 24.4609 47.4746 24.4609 48.2432C24.4609 49.0117 25.084 49.6348 25.8525 49.6348H45.0929C45.8614 49.6348 46.4845 49.0117 46.4845 48.2432C46.4845 47.4746 45.8614 46.8516 45.0929 46.8516Z"
                            fill="#CC8B00"
                        />
                        <path
                            d="M45.0929 52.9609H25.8525C25.084 52.9609 24.4609 53.584 24.4609 54.3525C24.4609 55.1211 25.084 55.7441 25.8525 55.7441H45.0929C45.8614 55.7441 46.4845 55.1211 46.4845 54.3525C46.4845 53.584 45.8614 52.9609 45.0929 52.9609Z"
                            fill="#CC8B00"
                        />
                        <path
                            d="M39.7137 59.1328H31.2432C30.4746 59.1328 29.8516 59.7559 29.8516 60.5244C29.8516 61.293 30.4746 61.916 31.2432 61.916H39.7137C40.4823 61.916 41.1053 61.293 41.1053 60.5244C41.1053 59.7559 40.4823 59.1328 39.7137 59.1328Z"
                            fill="#CC8B00"
                        />
                        <path
                            d="M38.3108 38.2961H33.8038C33.3073 38.2961 32.9062 37.8942 32.9062 37.3966V31.1386C32.9062 29.3971 34.3194 28 36.0382 28C37.7569 28 39.1701 29.4162 39.1701 31.1386V37.4158C39.1892 37.8942 38.7882 38.2961 38.3108 38.2961ZM34.7014 36.5163H37.4132V31.1386C37.4132 30.3922 36.8021 29.7798 36.0573 29.7798C35.3125 29.7798 34.7014 30.3922 34.7014 31.1386V36.5163Z"
                            fill="#CC8B00"
                        />
                        <path
                            d="M40.1666 39.9993H32.0122C31.4583 39.9993 31 39.54 31 38.985V33.569C31 33.014 31.4583 32.5547 32.0122 32.5547H40.1666C40.7205 32.5547 41.1788 33.014 41.1788 33.569V38.985C41.1788 39.54 40.7205 39.9993 40.1666 39.9993Z"
                            fill="#CC8B00"
                        />
                        <path
                            d="M36.94 35.5656C36.94 35.901 36.7488 36.1885 36.4778 36.3322V37.2586C36.4778 37.4822 36.3025 37.6579 36.0794 37.6579C35.8563 37.6579 35.6809 37.4822 35.6809 37.2586V36.3322C35.41 36.1885 35.2188 35.901 35.2188 35.5656C35.2188 35.0864 35.6013 34.7031 36.0794 34.7031C36.5575 34.7031 36.94 35.0864 36.94 35.5656Z"
                            fill="white"
                        />
                    </svg>
                )}
            </div>
            <div className={`${baseClass}__unavailable__top`}>
                <div
                    className={clsx(`${baseClass}__unavailable--title`, {
                        [`${baseClass}__unavailable--archived--title`]: unavailableStatusId === 3,
                        [`${baseClass}__unavailable--employees--title`]: unavailableStatusId === 2,
                        [`${baseClass}__unavailable--expired--title`]: unavailableStatusId === 1,
                        [`${baseClass}__unavailable--external--title`]: isExternal,
                    })}
                >
                    {!isUnavailable && isExternal && t('externalJobTitle', 'External application')}
                    {unavailableStatusId === 3 && (
                        <>
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M2.4338 1.66406C1.55132 1.66406 0.835938 2.28955 0.835938 3.06112V6.0023C0.835938 6.36778 1.1748 6.66406 1.59282 6.66406H18.4124C18.8304 6.66406 19.1693 6.36778 19.1693 6.0023V3.06112C19.1693 2.28955 18.4539 1.66406 17.5714 1.66406H2.4338ZM1.75269 8.5465C1.75269 8.20553 2.08847 7.92912 2.50269 7.92912H17.5027C17.9169 7.92912 18.2527 8.20553 18.2527 8.5465V16.2579C18.2527 17.3565 17.1707 18.2472 15.836 18.2472H4.16935C2.83466 18.2472 1.75269 17.3565 1.75269 16.2579V8.5465ZM6.75268 10.674C6.75268 10.2598 7.08847 9.92404 7.50268 9.92404H12.5027C12.9169 9.92404 13.2527 10.2598 13.2527 10.674C13.2527 11.0882 12.9169 11.424 12.5027 11.424H7.50268C7.08847 11.424 6.75268 11.0882 6.75268 10.674Z"
                                    fill="#CC8B00"
                                />
                            </svg>
                            <span>{t('9973', 'Job has been archived')}</span>
                        </>
                    )}
                    {unavailableStatusId === 1 && (
                        <>
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8.60696 2.50642C9.2424 1.46661 10.7526 1.46661 11.388 2.50642L19.4955 15.7732C20.1591 16.8591 19.3776 18.2526 18.105 18.2526H1.89C0.617374 18.2526 -0.164146 16.8591 0.499466 15.7732L8.60696 2.50642ZM9.9975 12.7897C9.58328 12.7897 9.2475 12.4539 9.2475 12.0397V7.29431C9.2475 6.8801 9.58328 6.54431 9.9975 6.54431C10.4117 6.54431 10.7475 6.8801 10.7475 7.29431V12.0397C10.7475 12.4539 10.4117 12.7897 9.9975 12.7897ZM10.0068 15.8544C10.5361 15.8544 10.9651 15.4254 10.9651 14.8961C10.9651 14.3668 10.5361 13.9378 10.0068 13.9378C9.47751 13.9378 9.04845 14.3668 9.04845 14.8961C9.04845 15.4254 9.47751 15.8544 10.0068 15.8544Z"
                                    fill="#C05E1D"
                                />
                            </svg>

                            <span>{t('8727', 'Job has expired')}</span>
                        </>
                    )}
                    {unavailableStatusId === 2 && (
                        <>
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8.60696 2.50642C9.2424 1.46661 10.7526 1.46661 11.388 2.50642L19.4955 15.7732C20.1591 16.8591 19.3776 18.2526 18.105 18.2526H1.89C0.617374 18.2526 -0.164146 16.8591 0.499466 15.7732L8.60696 2.50642ZM9.9975 12.7897C9.58328 12.7897 9.2475 12.4539 9.2475 12.0397V7.29431C9.2475 6.8801 9.58328 6.54431 9.9975 6.54431C10.4117 6.54431 10.7475 6.8801 10.7475 7.29431V12.0397C10.7475 12.4539 10.4117 12.7897 9.9975 12.7897ZM10.0068 15.8544C10.5361 15.8544 10.9651 15.4254 10.9651 14.8961C10.9651 14.3668 10.5361 13.9378 10.0068 13.9378C9.47751 13.9378 9.04845 14.3668 9.04845 14.8961C9.04845 15.4254 9.47751 15.8544 10.0068 15.8544Z"
                                    fill="#CC8B00"
                                />
                            </svg>

                            <span>{t('11237', 'Employees only')}</span>
                        </>
                    )}
                </div>
                <div
                    className={clsx(`${baseClass}__unavailable--description`, {
                        [`${baseClass}__unavailable--archived--title--description`]: unavailableStatusId === 3,
                        [`${baseClass}__unavailable--employees--title--description`]: unavailableStatusId === 2,
                        [`${baseClass}__unavailable--expired--title--description`]: unavailableStatusId === 1,
                        [`${baseClass}__unavailable--external--title--description`]: isExternal,
                    })}
                >
                    {!isUnavailable &&
                        isExternal &&
                        t('externalJobDescription', 'Applications for this job are handled on an external website.')}
                    {unavailableStatusId === 3 && (
                        <div>
                            <div>
                                {`${t('9974', 'This job posting has been archived and you can no longer apply')}.`}
                            </div>
                            <span>{t('8729', 'Click the button to view all available jobs.')}</span>
                        </div>
                    )}
                    {unavailableStatusId === 1 && (
                        <div>
                            <div>{t('8728', 'This job posting has expired and you can no longer apply.')}</div>{' '}
                            <span>{t('8729', 'Click the button to view all available jobs.')}</span>
                        </div>
                    )}
                    {unavailableStatusId === 2 && (
                        <div>
                            <div>
                                {`${t('11240', 'This job posting is visible only to employees who are logged in')}. `}
                            </div>
                            <div>{t('11241', 'External candidates will not see this posting')}.</div>
                        </div>
                    )}
                </div>
            </div>
            {isUnavailable && (
                <Link
                    href={'/jobs'}
                    className={clsx(`${baseClass}__unavailable__btn`, {
                        [`${baseClass}__unavailable__btn--archived`]: unavailableStatusId === 3,
                        [`${baseClass}__unavailable__btn--employees`]: unavailableStatusId === 2,
                        [`${baseClass}__unavailable__btn--expired`]: unavailableStatusId === 1,
                        [`${baseClass}__unavailable__btn--external`]: isExternal,
                    })}
                >
                    {t('8730', 'View all jobs')}
                </Link>
            )}
        </div>
    )

    if (isNotFoundError) {
        return <Page404 />
    }

    return (
        <div
            className={clsx(baseClass, {
                [`${baseClass}--has-unavailable`]: isUnavailable,
                [`${baseClass}--has-header-background`]: hasHeaderBackground,
            })}
        >
            {showButton && (
                <div className={`${baseClass}__apply`}>
                    {!isPreview && (
                        <div className={`${baseClass}__apply__inner`}>
                            <ApplySkeleton isLoading={isPostInfoLoader} width={'100%'} height={30} borderRadius="99999">
                                <Button
                                    variant="primary"
                                    className={`${baseClass}__apply-btn`}
                                    ra-data-job-post-id={id}
                                    id={'ra-button-job-post-action-apply-now'}
                                    onClick={() => onApply()}
                                    disabled={isPreview}
                                >
                                    {applyButtonTitle}
                                </Button>
                            </ApplySkeleton>
                        </div>
                    )}
                </div>
            )}
            <PostPageV2Header2
                postInfo={postInfoData}
                statusNotice={statusNotice}
                isLoading={isPostInfoLoader}
                postApplySettingsData={postApplySettingsData}
                applicationDetailsData={applicationDetailsData}
                isApplicationDetailsLoading={isApplicationDetailsLoader}
                isPreview={isPreview}
            />

            <div className={`${baseClass}__layout`}>
                <div className={`${baseClass}__container`}>
                    <div className={`${baseClass}__body`}>
                        <PostPageV2Info postInfo={postInfoData} isLoading={isPostInfoLoader} />
                        {!showCompanyAtBottom && aboutSection}
                        <PostPageV2Content postInfo={postInfoData} isLoading={isPostInfoLoader} />
                        {showCompanyAtBottom && aboutSection}
                        <PostPageV2Industries postInfo={postInfoData} isLoading={isPostInfoLoader} />
                        <PostPageV2ContactPersons postInfo={postInfoData} isLoading={isPostInfoLoader} />
                        <PostPageV2Files postInfo={postInfoData} isLoading={isPostInfoLoader} />
                        <PostPageV2Images postInfo={postInfoData} isLoading={isPostInfoLoader} />
                        <PostPageV2Video postInfo={postInfoData} isLoading={isPostInfoLoader} />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PostPageV2
